import React from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { styled } from '@mui/system';
import { useTranslate } from 'hooks/useTranslate';
import dayjs from 'dayjs';
import 'dayjs/locale/uk';

const StylerPicker = styled(DatePicker)(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-root': {
    borderRadius: '10px',
    minHeight: '40px'
  },
  '& .MuiInputBase-input': {
    padding: '2px 14px'
  },
  // Ensure calendar icon is visible on mobile
  '& .MuiInputAdornment-root': {
    display: 'flex !important',
    visibility: 'visible !important',
    opacity: '1 !important',
    '& .MuiIconButton-root': {
      display: 'flex !important',
      visibility: 'visible !important',
      opacity: '1 !important',
      padding: '8px',
      '& svg': {
        display: 'block !important',
        visibility: 'visible !important',
        opacity: '1 !important',
        width: '24px',
        height: '24px',
        [theme.breakpoints.down('sm')]: {
          width: '20px',
          height: '20px',
          color: theme.palette.primary.main // Green color on mobile
        }
      }
    }
  },
  '& .MuiSvgIcon-root': {
    display: 'block !important',
    visibility: 'visible !important',
    opacity: '1 !important',
    width: '24px !important',
    height: '24px !important',
    [theme.breakpoints.down('sm')]: {
      width: '20px !important',
      height: '20px !important',
      color: theme.palette.primary.main // Green color on mobile
    }
  }
}));

const CalendarTip = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  lineHeight: '20px',
  color: theme.palette.textPrimary?.main || theme.palette.text.primary || '#000',
  textAlign: 'center',
  padding: '10px 16px',
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background?.paper || theme.palette.grey[50] || '#fafafa'
}));

const CalendarPaper = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'column'
}));

const CustomPaper = React.forwardRef((props, ref) => {
  const t = useTranslate('Consultant');
  const { children, ...other } = props;
  return (
    <CalendarPaper ref={ref} {...other}>
      {children}
      <CalendarTip variant="caption">
        {t('SelectDateOfBirth')}
      </CalendarTip>
    </CalendarPaper>
  );
});

const CustomTextField = React.forwardRef((props, ref) => {
  // MUI X 9 hands the text-field slot a merged `slotProps` (input, htmlInput,
  // …) instead of the legacy `InputProps` / `inputProps`, which MUI 9's
  // TextField no longer accepts. Merge our additions into that shape so the
  // picker's own slot props survive.
  const { displayValue, InputProps = {}, slotProps = {}, onClick, ...other } = props;

  // Use displayValue if provided, otherwise use the value passed by DatePicker
  const finalValue = displayValue !== undefined ? displayValue : props.value;

  // Merge existing endAdornment with our calendar icon, or use our icon if none exists
  const endAdornment = InputProps.endAdornment || slotProps.input?.endAdornment || (
    <InputAdornment position="end">
      <IconButton aria-label="Choose date" edge="end" onClick={onClick}>
        <CalendarTodayIcon />
      </IconButton>
    </InputAdornment>
  );

  return (
    <TextField
      {...other}
      value={finalValue}
      ref={ref}
      slotProps={{
        ...slotProps,
        htmlInput: {
          ...slotProps.htmlInput,
          readOnly: displayValue !== undefined ? true : slotProps.htmlInput?.readOnly
        },
        input: {
          ...slotProps.input,
          ...InputProps,
          endAdornment
        }
      }}
    />
  );
});

const DatePickerComponent = ({
  value: valueProps,
  fullWidth,
  error,
  placeholder,
  helperText,
  onChange,
  key,
  open,
  onOpen,
  onClose,
  disabled,
  displayValue
}) => {
  // Convert valueProps to dayjs object or null, default to Jan 1, 2010
  const convertToDayjs = React.useCallback((val) => {
    if (!val || val === '') return dayjs('2010-01-01');
    // Check if it's already a dayjs object by checking for isValid method
    if (val && typeof val.isValid === 'function') {
      return val.isValid() ? val : dayjs('2010-01-01');
    }
    // Try to convert to dayjs
    const dayjsValue = dayjs(val);
    return dayjsValue.isValid() ? dayjsValue : dayjs('2010-01-01');
  }, []);

  const [value, setValue] = React.useState(() => convertToDayjs(valueProps));

  // Sync state when valueProps changes
  React.useEffect(() => {
    setValue(convertToDayjs(valueProps));
  }, [valueProps, convertToDayjs]);

  const handleChange = React.useCallback(
    (newValue) => {
      setValue(newValue);
      onChange(newValue);
    },
    [onChange]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'uk'}>
      <StylerPicker
        key={key}
        open={open}
        value={value}
        disableFuture={true}
        onChange={handleChange}
        onOpen={onOpen}
        onClose={onClose}
        disabled={disabled}
        slots={{
          paper: CustomPaper,
          desktopPaper: CustomPaper,
          mobilePaper: CustomPaper,
          textField: CustomTextField
        }}
        slotProps={{
          textField: {
            fullWidth: true,
            error,
            placeholder,
            helperText,
            displayValue
          }
        }}
      />
    </LocalizationProvider>
  );
};

export default DatePickerComponent;
